import { houseApi } from '../services/api';
import { logger } from '../services/logger';

svgUploadCtrl.$inject = [
  '$scope',
  'SvgUpload',
  '$rootScope',
  'Upload',
  '$translate',
  '$mdDialog',
  '$notification',
];

/**
 * @typedef {import('../types').RootScope} RootScope
 * @typedef {import('../types').Section} Section
 * @typedef {import('../types').ImageItem} ImageItem
 * @typedef {import('../section.svg.upload/types').ImageUploadResponse} ImageUploadResponse
 * @typedef {import('./types').SectionAvailability} SectionAvailability
 * @typedef {import('./types').SvgUpload} SvgUpload
 * @typedef {import('../ui/notification').UiNotificationService} UiNotificationService
 */

/**
 * @typedef {File & {$error?: string; $errorParam?: string}} NgFile
 *
 * @typedef {object} SvgUploadCtrlProps
 * @property {number} houseId
 * @property {string | null=} previewSvgUrl
 * @property {string | null=} previewImageUrl
 * @property {string | null=} finalSvgUrl
 * @property {string | null=} finalImageUrl
 * @property {ImageItem[]} sourceImages - image for navigation from client
 * @property {ImageItem[]} draftImages - schema describing how navigation should work from client
 * @property {ImageItem[]} previewImages - rescaled and production ready image for navigation from our operators
 * @property {boolean} savingInProgress
 * @property {number} progress
 * @property {string} naviLink
 * @property {{id: string; name: string}[]} availabilityStatuses
 * @property {Section[]} sections
 * @property {(files: NgFile[], type: 'naviHousePreviewSvg') => void} uploadFiles
 * @property {(event: MouseEvent, imageUrl: string | null, svgUrl: string | null) => void} showNaviPreview
 * @property {(id: string, availability: SectionAvailability) => void} updateSectionAvailability
 */

/**
 * @this {SvgUploadCtrlProps}
 *
 * @param {angular.IScope} $scope
 * @param {SvgUpload} SvgUpload
 * @param {RootScope} $rootScope
 * @param {any} Upload
 * @param {angular.translate.ITranslateService} $translate
 * @param {angular.material.IDialogService} $mdDialog
 * @param {UiNotificationService} $notification
 */
export function svgUploadCtrl(
  $scope,
  SvgUpload,
  $rootScope,
  Upload,
  $translate,
  $mdDialog,
  $notification,
) {
  const vm = this;

  vm.sourceImages = vm.sourceImages || [];
  vm.draftImages = vm.draftImages || [];
  vm.previewImages = vm.previewImages || [];
  vm.savingInProgress = false;

  vm.availabilityStatuses = [
    { id: '0', name: $translate.instant('general.filters.add') },
    { id: '1', name: $translate.instant('navi.not_in_stock') },
    { id: '2', name: $translate.instant('navi.incoming') },
    { id: '3', name: $translate.instant('navi.in_stock') },
    { id: '4', name: $translate.instant('navi.all_sold') },
  ];

  vm.naviLink = `${window.location.origin}/navi/${vm.houseId}`;

  $scope.$watch(
    () => vm.previewImages.length,
    () => {
      if (vm.previewImages.length) {
        vm.previewImageUrl = vm.previewImages[
          vm.previewImages.length - 1
        ].url.replace(/^http:/, 'https:');
      }
    },
  );

  houseApi.getSections(vm.houseId).then((result) => {
    vm.sections = result;

    $scope.$apply();
  });

  SvgUpload.fetchImages({
    houseId: vm.houseId,
    finalOnly: false,
  }).then((images) => {
    $scope.$apply(() => {
      vm.previewSvgUrl = images.previewSvg;
      vm.finalSvgUrl = images.finalSvg;
      vm.finalImageUrl = images.finalImage;
    });
  });

  /** @type {SvgUploadCtrlProps['uploadFiles']} */
  vm.uploadFiles = (files, type) => {
    // TODO: move to service
    const [file] = files;

    if (!file) {
      return;
    }

    if (file.$error && file.$error === 'maxSize') {
      $notification.show({
        type: 'error',
        text: `Your file exceeds ${file.$errorParam}. Please, resize.`,
      });

      return;
    }

    Upload.upload({
      method: 'POST',
      url: '/images/upload?act=submit',
      file,
      fields: {
        type,
        id: vm.houseId,
        token: $rootScope.token,
      },
    })
      .progress((evt) => {
        vm.progress = Math.floor((100.0 * evt.loaded) / evt.total);
      })
      .then((/** @type {{ data: ImageUploadResponse }} */ response) => {
        vm.progress = 0;

        if (!response.data.status) {
          throw Object.assign(new Error('Invalid response status'), {
            response,
          });
        }

        vm.previewSvgUrl = response.data.result.url.replace(/^http:/, 'https:');

        $notification.show({
          type: 'success',
          text: $translate.instant('svg.upload.success'),
        });
      })
      .catch((error) => {
        $notification.show({
          type: 'error',
          text: `${$translate.instant('svg.upload.format.error')}${
            error.message || ''
          }`,
        });

        logger.error('Error uploading image', {
          error,
        });
      });
  };

  /** @type {SvgUploadCtrlProps['updateSectionAvailability']} */
  vm.updateSectionAvailability = (id, availability) => {
    vm.savingInProgress = true;

    SvgUpload.updateSectionAvailability(
      {
        id,
      },
      {
        availability,
      },
      () => {
        vm.savingInProgress = false;
      },
    );
  };

  /** @type {SvgUploadCtrlProps['showNaviPreview']} */
  vm.showNaviPreview = (event, imageUrl, svgUrl) => {
    if (!imageUrl || !svgUrl) {
      return;
    }

    $mdDialog.show({
      targetEvent: event,
      controller: [
        '$mdDialog',
        function DialogController($mdDialog) {
          this.close = () => {
            $mdDialog.hide();
          };

          this.publish = async () => {
            try {
              await SvgUpload.approveImages(
                {
                  id: vm.houseId,
                },
                {},
              ).$promise;

              $scope.$apply(() => {
                vm.finalImageUrl = vm.previewImageUrl;
                vm.finalSvgUrl = vm.previewSvgUrl;
                vm.previewSvgUrl = null;
                vm.sourceImages = [];
                vm.draftImages = [];
                vm.previewImages = [];
              });

              $notification.show({
                type: 'success',
                text: $translate.instant('navi.publish.success'),
              });

              this.close();
            } catch (error) {
              $notification.show({
                type: 'error',
                text: $translate.instant('navi.publish.error'),
              });

              logger.error('Error publishing navigation', {
                error,
              });
            }
          };
        },
      ],
      bindToController: true,
      controllerAs: 'vm',
      locals: {
        houseId: vm.houseId,
        svgUrl: vm.previewSvgUrl,
        imageUrl: vm.previewImageUrl,
        published: !!vm.finalSvgUrl && svgUrl === vm.finalSvgUrl,
      },
      template: require('../svg.upload/preview.dialog.html'),
    });
  };
}
